<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div
            class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
            <div class="d-sm-flex align-items-center">
                <router-link :to="`/content-category/${category_id}/create-content`"
                    class="default-btn position-relative transition border-0 fw-medium text-white pt-11 pb-11 ps-25 pe-25 pt-md-12 pb-md-12 ps-md-30 pe-md-30 rounded-1 bg-success fs-md-15 fs-lg-16 d-inline-block text-decoration-none">
                    {{ $t('cta.create_content') }}
                    <i class="ph-bold ph-plus position-relative ms-5 fs-12"></i>
                </router-link>
            </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
            <SpinnerLoading v-if="contents === undefined" class="mt-50" />
            <div class="table-responsive" v-else>
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                ID
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                {{ $t('form.title.label') }}
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                {{ $t('form.active.label') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                            </th>
                        </tr>
                    </thead>
                    <tbody class="linked">
                        <!-- CENTER ROW -->
                        <tr v-for="content in contents" :key="'tr_center_' + content.id">
                            <td class="shadow-none lh-1 fw-medium text-primary" @click="show(content)">
                                {{ content.id }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis" @click="show(content)">
                                {{ content.title }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                <div class="form-check form-switch">
                                    <input class="form-check-input shadow-none me-8" type="checkbox" role="switch" v-model="content.active" :false-value="0" :true-value="1"
                                        v-on:change="changeActive(content)">
                                </div>
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                                <div class="dropdown position-static">
                                    <button class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                                        type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="viewport">
                                        <i class="flaticon-dots"></i>
                                    </button>
                                    <ul class="dropdown-menu">
                                        <!-- <li>
                                            <router-link :to="{ path: `/content/${content.id}` }"
                                                class="dropdown-item d-flex align-items-center">
                                                <i class="ph ph-eye lh-1 me-8 position-relative top-1"></i>
                                                {{ $t('cta.view') }}
                                            </router-link>
                                        </li> -->
                                        <li>
                                            <router-link :to="{ path: `/content/${content.id}/edit` }"
                                                class="dropdown-item d-flex align-items-center">
                                                <i class="ph-duotone ph-pencil lh-1 me-8 position-relative top-1"></i>
                                                {{ $t('cta.edit') }}
                                            </router-link>
                                        </li>
                                        <li>
                                            <button class="dropdown-item d-flex align-items-center"
                                                v-on:click="confirmDeleteContent(content)">
                                                <i class="ph ph-trash lh-1 me-8 position-relative top-1"></i>
                                                {{ $t('cta.delete') }}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- PAGINATION -->
            <TablePagination :count="contents?.length ?? 0" :page="currentPage" :total="total" :pages="lastPage"
                @change="setPage($event)" />
        </div>
    </div>
    <ConfirmModal @confirm="deleteContent()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>
    
<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import API from "@/services/api/api.service";
import { Modal } from "bootstrap";
import TablePagination from "@/components/FK/TablePagination.vue";
import { ContentInfo } from "@/utils/models/content.model";
import { bool_to_int } from "@/utils/methods/int_to_bool";

const PAGE_SIZE = 10;

export default defineComponent({
    name: "ContentCategoryList",
    data: () => {
        return {
            contents: undefined,
            currentPage: 1,
            lastPage: 1,
            total: 0,
            perPage: PAGE_SIZE,
            confirmModal: null,
            selectedContent: null
        } as {
            contents?: ContentInfo[],
            currentPage: number,
            lastPage: number,
            total: number,
            perPage: number,
            confirmModal: Modal | null,
            selectedContent: ContentInfo | null;
        }
    },
    computed: {
        category_id() { return this.$route.params.id }
    },
    mounted() {
        this.loadContents(1);
        this.confirmModal = new Modal('#confirmModal', {})
    },
    methods: {
        loadContents(page: number) {
            this.contents = undefined;
            API.getContents({ page: page, category_id: this.$route.params.id! as string }).then((res) => {
                this.contents = res.data.data;
                this.total = res.data.total;
                this.lastPage = res.data.last_page;
            })
        },
        setPage(page: number) {
            this.currentPage = page;
            this.loadContents(page);
        },
        confirmDeleteContent(content: ContentInfo) {
            this.selectedContent = content;
            this.$data.confirmModal?.show();
        },
        deleteContent(): void {
            API.deleteContent(this.selectedContent?.id!).then((response) => {
                if (response.data.success === true) {
                    this.selectedContent = null;
                    this.setPage(1);
                }
            }).finally(() => {
                console.log("DELETED_CONTENT_ID", this.selectedContent?.id);
            })
        },
        changeActive(content: ContentInfo) {
            console.log(content.active)
            
            API.setActiveContent({content_id: content.id!, active: content.active as 0 | 1}).catch((error) => {
                content.active = bool_to_int(!content.active);
            })
        },
        show(content: ContentInfo) {
            this.$router.push({ path: `/content/${content.id}/edit` })
        }
    },
    components: {
        ConfirmModal,
        SpinnerLoading,
        TablePagination
    }
});
</script>